import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@any-ui-react/core';
import { vestResolver } from '@hookform/resolvers/vest';

import { Spinner, toast } from '@anyx/shared-ui';


import { DEFAULT_PLAIN_TEXT_BLOCK, LineMessageForm } from '../../components';
import { useSerializeDraftUnitMessage } from '../../hooks';
import { setMessagesToGQLLineContent } from '../../utils/line-message-adapter';

import { FormView } from './form-view';
import { useLineMessageDialogFormValidate } from './hooks';
import { LineMessageDialogFormFields, LineMessageDialogProps } from './types';

const FORM_ID = 'line-message-dialog-form';

export const LineMessageDialog = ({
  onSubmit,
  messageId,
  onCancel,
  initialValues,
  ...storeProps
}: LineMessageDialogProps) => {
  const { t } = useTranslation(['crm', 'shared']);
  const { validationSuite } = useLineMessageDialogFormValidate();
  const { serializeDraftUnitMessage } = useSerializeDraftUnitMessage();

  const { handleSubmit, formState, ...methods } = useForm<LineMessageDialogFormFields>({
    resolver: vestResolver(validationSuite),
    defaultValues: {
      title: initialValues?.title ?? '',
      messages: initialValues?.messages ?? [DEFAULT_PLAIN_TEXT_BLOCK],
    },
  });

  const { isLoading, isSubmitting } = formState;

  const submitLineMessageDialogForm = handleSubmit(
    async (data) => {
      const updatedDraftContent = await serializeDraftUnitMessage({
        name: data.title,
        messageItems: setMessagesToGQLLineContent(data.messages),
      });

      if (updatedDraftContent) {
        onSubmit({ updatedDraftContent, lineMessageDialog: data });
      }
    },
    (errors) => {
      if ('title' in errors) {
        toast.error(t('crm.lineMessageDialogForm.notification.error.title', { ns: 'crm' }));
      }
    }
  );

  if (isLoading) {
    return (
      <div className="flex h-56 w-56 items-center justify-center">
        {isLoading ? <Spinner /> : t('shared.alert.error.default', { ns: 'shared' })}
      </div>
    );
  }

  return (
    <LineMessageForm.Root
      methods={{ ...methods, formState, handleSubmit }}
      onLeavePage={onCancel}
      title={t('crm.lineMessageDialogForm.title', { ns: 'crm' })}
      messageId={messageId}
      headerAside={
        <LineMessageForm.ButtonGroup>
          {onCancel && (
            <Button variant="default" onClick={onCancel}>
              {t('shared.button.cancel', { ns: 'shared' })}
            </Button>
          )}
          <Button loading={isSubmitting} type="submit" form={FORM_ID}>
            {t('shared.button.save', { ns: 'shared' })}
          </Button>
        </LineMessageForm.ButtonGroup>
      }
      {...storeProps}
    >
      <FormView onSubmit={submitLineMessageDialogForm} id={FORM_ID} />
      <LineMessageForm.Preview mobilePreviewClass="w-80" />
    </LineMessageForm.Root>
  );
};
